import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Clock, Users, Award } from 'lucide-react';
import { allModules } from '@/data/courseModules';
import { getCheckoutUrlWithUtms } from '@/lib/checkout';
const ProfessionalCourseContent = () => {
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const scrollToProduct = () => {
    const productSection = document.getElementById('cta');
    if (productSection) {
      productSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const toggleModule = (moduleId: string) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  // Adapt module descriptions for professional context
  const getProfessionalDescription = (originalDescription: string, moduleId: string) => {
    const professionalDescriptions: {
      [key: string]: string;
    } = {
      "module-1": "Fundamentos científicos essenciais para orientação profissional em aleitamento materno",
      "module-2": "Aplicação prática do Método PPS em consultorias e atendimentos clínicos",
      "module-3": "Diagnóstico profissional de dificuldades e problemas na amamentação",
      "module-4": "Intervenções técnicas especializadas para resolução de problemas complexos",
      "module-5": "Atendimento especializado em situações específicas e casos especiais",
      "module-6": "Orientação profissional sobre livre demanda e manejo clínico",
      "module-7": "Prescrição e orientação profissional sobre equipamentos e dispositivos",
      "module-8": "Base científica para desmistificação e educação de pacientes",
      "module-9": "Orientação profissional sobre sono e amamentação para famílias",
      "module-10": "Cuidados especializados do recém-nascido para profissionais",
      "module-11": "Orientação profissional sobre introdução alimentar e amamentação",
      "module-12": "Recursos complementares para prática profissional especializada"
    };
    return professionalDescriptions[moduleId] || originalDescription;
  };
  return <section id="course-content" data-section="course-content" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Conteúdo Programático Completo
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
            10 módulos especializados com mais de 100 aulas práticas, 
            desenvolvidos especificamente para capacitação profissional
          </p>
          
          {/* Course Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 bg-white rounded-xl p-4 shadow-sm">
              <Clock className="h-6 w-6 text-primary" />
              <div className="text-left">
                <div className="font-bold text-gray-900">25 Horas</div>
                <div className="text-sm text-gray-600">de Aula</div>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-3 bg-white rounded-xl p-4 shadow-sm">
              <Users className="h-6 w-6 text-primary" />
              <div className="text-left">
                <div className="font-bold text-gray-900">Casos Reais</div>
                <div className="text-sm text-gray-600">Demonstrações</div>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-3 bg-white rounded-xl p-4 shadow-sm">
              <Award className="h-6 w-6 text-primary" />
              <div className="text-left">
                <div className="font-bold text-gray-900">Certificado</div>
                <div className="text-sm text-gray-600">Profissional</div>
              </div>
            </div>
          </div>
        </div>

        {/* Modules */}
        <div className="space-y-4 mb-12">
          {allModules.map((module, index) => <Card key={module.id} className="border border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden rounded-xl">
              <CardHeader className="cursor-pointer bg-white hover:bg-gray-50 transition-colors duration-200" onClick={() => toggleModule(module.id)}>
                {/* Mobile Layout */}
                <div className="flex md:hidden flex-col gap-3">
                  {/* Line 1: Icon and Title */}
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 rounded-xl p-2 text-primary flex-shrink-0">
                      {module.icon}
                    </div>
                    <CardTitle className="text-base font-bold text-gray-900 flex-1">
                      {module.title}
                    </CardTitle>
                    {expandedModule === module.id ? <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />}
                  </div>
                  
                  {/* Line 2: Description and Lessons Count */}
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-gray-600 text-sm flex-1">
                      {getProfessionalDescription(module.description, module.id)}
                    </p>
                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full flex-shrink-0">
                      {module.topics.length} aulas
                    </span>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/10 rounded-xl p-3 text-primary">
                      {module.icon}
                    </div>
                    <div className="text-left">
                      <CardTitle className="text-lg font-bold text-gray-900 mb-1">
                        {module.title}
                      </CardTitle>
                      <p className="text-gray-600 text-sm">
                        {getProfessionalDescription(module.description, module.id)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                      {module.topics.length} aulas
                    </span>
                    {expandedModule === module.id ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
                  </div>
                </div>
              </CardHeader>
              
              {expandedModule === module.id && <CardContent className="bg-gray-50 border-t">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {module.topics.map((topic, topicIndex) => <div key={topicIndex} className="flex items-start gap-3 py-1.5">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm">{topic}</span>
                      </div>)}
                  </div>
                  
                  {/* Professional Focus Note */}
                  <div className="mt-4 p-4 bg-primary/5 rounded-xl">
                    <p className="text-sm text-gray-700">
                      <strong>Foco Profissional:</strong> Todas as aulas incluem orientações específicas 
                      para aplicação em consultorias, atendimentos clínicos e orientação de pacientes.
                    </p>
                  </div>
                </CardContent>}
            </Card>)}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Pronto para Se Tornar um Especialista?
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            Acesso vitalício a todo conteúdo + certificado profissional + suporte técnico
          </p>
          <Button 
            size="lg" 
            className="bg-accent-ink hover:bg-accent-ink-dark text-white font-semibold px-8 py-4 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all duration-300" 
            onClick={() => window.location.href = getCheckoutUrlWithUtms('https://pay.hotmart.com/T84177472Y?off=s3ddbg8f&checkoutMode=10')}
          >
            Quero me Especializar
          </Button>
        </div>
      </div>
    </section>;
};
export default ProfessionalCourseContent;